<template>
  <v-card
    :color="cardColor"
    :dark="hasRisk"
    class="mb-4"
  >
    <v-card-title class="d-flex justify-space-between align-center py-3">
      <div class="d-flex align-center">
        <v-icon
          :color="hasRisk ? 'white' : 'grey'"
          class="mr-2"
        >
          {{ icons.mdiAlertCircle }}
        </v-icon>
        <span>離反リスク</span>
      </div>
      <v-select
        v-model="selectedThreshold"
        :items="thresholdOptions"
        item-text="text"
        item-value="value"
        dense
        outlined
        hide-details
        :dark="hasRisk"
        style="max-width: 120px;"
        @change="handleThresholdChange"
      />
    </v-card-title>

    <v-card-text :class="{ 'white--text': hasRisk }">
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        :color="hasRisk ? 'white' : 'primary'"
        class="mb-4"
      />

      <template v-else>
        <div class="d-flex justify-space-between align-center mb-3">
          <div>
            <div class="text-h4 font-weight-bold">
              {{ summary.totalAtRiskCount }}
              <span class="text-body-2">名</span>
            </div>
            <div class="text-caption">
              {{ selectedThreshold }}日以上未来店
            </div>
          </div>
          <div
            v-if="summary.highValueAtRiskCount > 0"
            class="text-right"
          >
            <div class="text-h6 font-weight-bold warning--text">
              {{ summary.highValueAtRiskCount }}名
            </div>
            <div class="text-caption">
              高単価顧客
            </div>
          </div>
        </div>

        <v-btn
          v-if="hasRisk"
          small
          outlined
          :color="hasRisk ? 'white' : 'primary'"
          @click="showDetailDialog = true"
        >
          詳細を見る
        </v-btn>

        <div
          v-else
          class="text-center py-2"
        >
          <v-icon color="success">
            {{ icons.mdiCheckCircle }}
          </v-icon>
          <span class="ml-2 success--text">離反リスク顧客なし</span>
        </div>
      </template>
    </v-card-text>

    <!-- 詳細ダイアログ -->
    <v-dialog
      v-model="showDetailDialog"
      max-width="700"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>離反リスク顧客一覧</span>
          <v-btn
            icon
            @click="showDetailDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          {{ selectedThreshold }}日以上来店のない顧客
        </v-card-subtitle>
        <v-card-text>
          <v-simple-table dense>
            <template #default>
              <thead>
                <tr>
                  <th>顧客名</th>
                  <th class="text-right">
                    最終来店日
                  </th>
                  <th class="text-right">
                    未来店日数
                  </th>
                  <th class="text-right">
                    累計購入額
                  </th>
                  <th class="text-right">
                    来店回数
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="customer in customers"
                  :key="customer.customerId"
                  class="cursor-pointer"
                  @click="handleCustomerClick(customer)"
                >
                  <td>
                    {{ customer.customerName }}
                    <v-chip
                      v-if="customer.totalPurchaseAmount >= 100000"
                      x-small
                      color="warning"
                      class="ml-1"
                    >
                      高単価
                    </v-chip>
                  </td>
                  <td class="text-right">
                    {{ formatDate(customer.lastVisitedAt) }}
                  </td>
                  <td class="text-right">
                    <span class="error--text font-weight-bold">
                      {{ customer.daysSinceLastVisit }}日
                    </span>
                  </td>
                  <td class="text-right">
                    {{ formatCurrency(customer.totalPurchaseAmount) }}
                  </td>
                  <td class="text-right">
                    {{ customer.visitCount }}回
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  ref, watch, computed,
} from '@vue/composition-api'
import { mdiAlertCircle, mdiCheckCircle, mdiClose } from '@mdi/js'
import { formatDate, formatCurrency } from '@/utils/formatters'
import useChurnRisk, { THRESHOLD_OPTIONS } from '../composables/useChurnRisk'

export default {
  name: 'ChurnRiskAlert',
  setup(props, { root, emit }) {
    const {
      customers,
      summary,
      loading,
      fetchChurnRisk,
    } = useChurnRisk(root)

    const selectedThreshold = ref(30)
    const showDetailDialog = ref(false)

    const isLoading = computed(() => loading.value)
    const hasRisk = computed(() => summary.value.totalAtRiskCount > 0)
    const cardColor = computed(() => {
      if (hasRisk.value) return 'error'

      return undefined
    })

    const thresholdOptions = THRESHOLD_OPTIONS

    const loadChurnRisk = () => {
      fetchChurnRisk({ threshold: selectedThreshold.value })
    }

    const handleThresholdChange = () => {
      loadChurnRisk()
    }

    const handleCustomerClick = customer => {
      showDetailDialog.value = false
      emit('customer-click', customer.customerId)
    }

    // 初回ロード
    loadChurnRisk()

    return {
      customers,
      summary,
      isLoading,
      hasRisk,
      cardColor,
      selectedThreshold,
      showDetailDialog,
      thresholdOptions,
      formatCurrency,
      formatDate,
      handleThresholdChange,
      handleCustomerClick,
      icons: {
        mdiAlertCircle,
        mdiCheckCircle,
        mdiClose,
      },
    }
  },
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
